import { triadSuperFetch } from 'utils/superFetch';
import { createThrottledQueue } from 'utils/createThrottledQueue';
import { TRIAD_PROXY_ROUTE } from 'app-requests/apiConstants';
import isBrowser from 'utils/isBrowser';
import { getPageViewId, getUserSessionId } from 'utils/analyticsHelpers';
import {
  GA_EVENTS_MAP,
  GA_TO_TMS_EVENT_MAP,
  TMS_EVENT_TYPES,
} from './EVENT_TYPES';

const PENDING_EVENT_FLUSH_INTERVAL_MS = 1000;
const MAX_PENDING_EVENTS = 200;

interface PendingEvent {
  eventName: TMS_EVENT_TYPES;
  eventData: Record<string, string>[];
}

const throttledLogEvent = createThrottledQueue(
  async (eventName: TMS_EVENT_TYPES, eventData: Record<string, string>[]) => {
    await triadSuperFetch(`${TRIAD_PROXY_ROUTE}/logevent`, {
      method: 'POST',
      timeout: 2000,
      body: {
        eventName,
        eventData: Object.keys(eventData).map((key) => ({
          name: key,
          value: eventData[key as keyof typeof eventData],
        })),
      },
    });
  },
  400
);

const pendingEvents: PendingEvent[] = [];
let pendingEventsFlushTimer: ReturnType<typeof setInterval> | null = null;

function canLogTmsEvent(): boolean {
  return Boolean(getUserSessionId() && getPageViewId());
}

function sendEvent(
  eventName: TMS_EVENT_TYPES,
  eventData: Record<string, string>[]
): void {
  try {
    throttledLogEvent(eventName, eventData);
  } catch {
    // don't report
  }
}

function stopPendingEventsFlushTimer(): void {
  if (!pendingEventsFlushTimer) {
    return;
  }
  clearInterval(pendingEventsFlushTimer);
  pendingEventsFlushTimer = null;
}

function flushPendingEventsIfPossible(): void {
  if (!isBrowser() || !canLogTmsEvent()) {
    return;
  }
  while (pendingEvents.length > 0) {
    const nextEvent = pendingEvents.shift();
    if (nextEvent) {
      sendEvent(nextEvent.eventName, nextEvent.eventData);
    }
  }
  stopPendingEventsFlushTimer();
}

function ensurePendingEventsFlushTimer(): void {
  if (pendingEventsFlushTimer || !isBrowser()) {
    return;
  }
  pendingEventsFlushTimer = setInterval(() => {
    if (pendingEvents.length === 0) {
      stopPendingEventsFlushTimer();
      return;
    }
    flushPendingEventsIfPossible();
  }, PENDING_EVENT_FLUSH_INTERVAL_MS);
}

function enqueueEvent(eventName: TMS_EVENT_TYPES, eventData: Record<string, string>[]): void {
  if (pendingEvents.length >= MAX_PENDING_EVENTS) {
    pendingEvents.shift();
  }
  pendingEvents.push({ eventName, eventData });
  ensurePendingEventsFlushTimer();
}

export function logTmsEvent(
  eventName: TMS_EVENT_TYPES,
  eventData: Record<string, string>[] = []
): void {
  if (!isBrowser()) {
    return;
  }

  if (!canLogTmsEvent()) {
    enqueueEvent(eventName, eventData);
    return;
  }

  flushPendingEventsIfPossible();
  sendEvent(eventName, eventData);
}

export function logTGEventAsTmsEvent(
  eventName: GA_EVENTS_MAP,
  eventData: Record<string, string>[] = []
): void {
  const mappedEventName = GA_TO_TMS_EVENT_MAP[eventName];
  if (!mappedEventName) {
    return;
  }

  if (!isBrowser()) {
    return;
  }

  if (!canLogTmsEvent()) {
    enqueueEvent(mappedEventName, eventData);
    return;
  }

  flushPendingEventsIfPossible();
  sendEvent(mappedEventName, eventData);
}

export function logTmsButtonClick(buttonLabel: string): void {
  if (!buttonLabel) {
    return;
  }
  const eventName = `UserVisit.ButtonClick.${buttonLabel
    .toLowerCase()
    .replace(/ /g, '_')
    .replace(/'|"|:|;|,|\./g, '')}`;
  logTmsEvent(eventName as TMS_EVENT_TYPES);
}

// test helpers
export function __clearPendingEventsForTest(): void {
  pendingEvents.length = 0;
  stopPendingEventsFlushTimer();
}
